import { Controller, useForm } from "react-hook-form";
import { Dialog } from "components/Dialog";
import { Button } from "components/Button";
import { useTranslation } from "react-i18next";
import { TextField } from "@mui/material";
import { useState } from "react";
import { DatePicker, useDateForQueryParams } from "components/DatePicker";
import { TApartmentGridRow } from "components/Grid/grids/ApartmentsGrid/types";
import { TWaterMetersGridRow } from "components/Grid/grids/WaterMetersGrid/types";
import clsx from "clsx";
import { MetersChooserModal } from "components/MetersChooserModal";
import { ApartmentsChooserModal } from "components/ApartmentsChooserModal";

type TProps = {
  isOpen?: boolean;
  onClose: () => void;
  onSubmit: (value: TAddFormTypes) => void;
  isLoading: boolean;
};

export type TAddFormTypes = {
  apartmentId: string;
  floorNo: number;
  unitNo: string;
  identifierId: string;
  subscriberName: string;
  nationalId: string;
  phoneNumber: string;
  mobileNumber: string;
  postalCode: string;
  email: string;
  waterMeterId: string;
  installationDT: Date;
};

export function AddSubscriberModal(props: TProps) {
  const { isOpen = false, onClose, onSubmit, isLoading } = props;

  const { t } = useTranslation();
  const [convertDateForQueryParam] = useDateForQueryParams();

  const [isApartmentsModalOpen, setIsApartmentsModalOpen] = useState(false);
  const [selectedApartment, setSelectedApartment] =
    useState<TApartmentGridRow>();
  const [selectedMeter, setSelectedMeter] = useState<TWaterMetersGridRow>();
  const [isMetersModalOpen, setIsMetersModalOpen] = useState(false);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    getValues,
  } = useForm<TAddFormTypes>({
    defaultValues: {
      apartmentId: undefined,
      floorNo: undefined,
      unitNo: undefined,
      identifierId: undefined,
      subscriberName: undefined,
      nationalId: undefined,
      phoneNumber: undefined,
      mobileNumber: undefined,
      postalCode: undefined,
      email: undefined,
      waterMeterId: undefined,
      installationDT: undefined,
    },
  });

  const onSelectApartmentClick = (value: any) => {
    setIsApartmentsModalOpen(true);
  };
  const onChooseApartment = (value: TApartmentGridRow) => {
    setSelectedApartment(value);
    setValue("apartmentId", value?.id);
    setIsApartmentsModalOpen(false);
  };
  const onChooseMeter = (value: TWaterMetersGridRow) => {
    setSelectedMeter(value);
    setValue("waterMeterId", value?.id);
    setIsMetersModalOpen(false);
  };
  const onSelectMeterClick = () => {
    setIsMetersModalOpen(true);
  };

  const renderSelectedApartmentData = () => {
    if (selectedApartment) {
      return (
        <>
          <p className={"mr-2 text-gray-110"}>
            {t("selectedApartmentIdentifierId")}:
          </p>
          <p className={"mr-2 text-primary-60"}>
            {selectedApartment?.identifierId}
          </p>
        </>
      );
    } else {
      return (
        <p
          className={clsx(
            "mr-2 text-gray-90",
            errors.apartmentId && "!text-error-80"
          )}
        >
          {t("nothingSelected")}
        </p>
      );
    }
  };
  const renderSelectedMeterData = () => {
    if (selectedMeter) {
      return (
        <>
          <p className={"mr-2 text-gray-110"}>{t("selectedMeterSerialNo")}:</p>
          <p className={"mr-2 text-primary-60"}>{selectedMeter?.serialNo}</p>
        </>
      );
    } else {
      return (
        <p
          className={clsx(
            "mr-2 text-gray-90",
            errors.waterMeterId && "!text-error-80"
          )}
        >
          {t("nothingSelected")}
        </p>
      );
    }
  };

  return (
    <>
      <Dialog
        maxWidth={false}
        dialogTitle={t("addSubscriber")}
        open={isOpen}
        onClose={onClose}
        canClose={!isLoading}
      >
        <form className="p-4 w-[720px]" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col w-full gap-y-3 px-2">
            <p className={"font-bold text-primary-80"}>{t("identityInfo")}</p>
            <div className={"grid grid-cols-3 gap-x-2 gap-y-3 px-2"}>
              <Controller
                name={"subscriberName"}
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <TextField
                      label={t("subscriberName")}
                      placeholder={t("subscriberName")}
                      size="small"
                      {...field}
                      error={Boolean(error)}
                      className={"col-span-1"}
                    />
                  );
                }}
              />
              <Controller
                name={"nationalId"}
                control={control}
                rules={
                  {
                    // required: true,
                  }
                }
                render={({ field, fieldState: { error } }) => {
                  return (
                    <TextField
                      label={t("nationalId")}
                      placeholder={t("nationalId")}
                      size="small"
                      {...field}
                      error={Boolean(error)}
                      className={"col-span-1"}
                    />
                  );
                }}
              />
              <Controller
                name={"identifierId"}
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <TextField
                      label={t("subscriberIdentifierId")}
                      placeholder={t("subscriberIdentifierId")}
                      size="small"
                      {...field}
                      error={Boolean(error)}
                      className={"col-span-1"}
                    />
                  );
                }}
              />
            </div>
            <p className={"font-bold text-primary-80 mt-3"}>{t("callInfo")}</p>
            <div className={"grid grid-cols-3 gap-x-2 px-2"}>
              <Controller
                name={"phoneNumber"}
                control={control}
                rules={
                  {
                    // required: true,
                  }
                }
                render={({ field, fieldState: { error } }) => {
                  return (
                    <TextField
                      label={t("phoneNumber")}
                      placeholder={t("phoneNumber")}
                      size="small"
                      {...field}
                      error={Boolean(error)}
                      className={"col-span-1"}
                    />
                  );
                }}
              />
              <Controller
                name={"mobileNumber"}
                control={control}
                rules={
                  {
                    // required: true,
                  }
                }
                render={({ field, fieldState: { error } }) => {
                  return (
                    <TextField
                      label={t("mobileNumber")}
                      placeholder={t("mobileNumber")}
                      size="small"
                      {...field}
                      error={Boolean(error)}
                      className={"col-span-1"}
                    />
                  );
                }}
              />
              <Controller
                name={"email"}
                control={control}
                rules={
                  {
                    // required: true,
                  }
                }
                render={({ field, fieldState: { error } }) => {
                  return (
                    <TextField
                      label={t("email")}
                      placeholder={t("email")}
                      size="small"
                      {...field}
                      error={Boolean(error)}
                      className={"col-span-1"}
                    />
                  );
                }}
              />
            </div>
            <p className={"font-bold text-primary-80 mt-3"}>
              {t("locationInfo")}
            </p>
            <div className={"grid grid-cols-3 gap-x-2 gap-y-3 px-2"}>
              <Controller
                name={"apartmentId"}
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <div
                      className={
                        "flex w-full justify-between items-center col-span-3 border-[1px] border-gray-30 rounded-sm px-4 py-2"
                      }
                    >
                      <div className={"flex"}>
                        <p className={"font-bold text-gray-110 w-12"}>
                          {t("apartment")}
                        </p>
                        {renderSelectedApartmentData()}
                      </div>
                      <Button
                        variant="outlined"
                        onClick={onSelectApartmentClick}
                        className={"w-[106px]"}
                      >
                        {watch("apartmentId")
                          ? t("replaceApartment")
                          : t("chooseApartment")}
                      </Button>
                    </div>
                  );
                }}
              />
              <Controller
                name={"postalCode"}
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <TextField
                      label={t("postalCode")}
                      placeholder={t("postalCode")}
                      size="small"
                      {...field}
                      error={Boolean(error)}
                      className={"col-span-1"}
                    />
                  );
                }}
              />
              <Controller
                name={"floorNo"}
                control={control}
                rules={
                  {
                    // required: true,
                  }
                }
                render={({ field, fieldState: { error } }) => {
                  return (
                    <TextField
                      label={t("floorNo")}
                      placeholder={t("floorNo")}
                      size="small"
                      type="number"
                      {...field}
                      error={Boolean(error)}
                      className={"col-span-1"}
                    />
                  );
                }}
              />
              <Controller
                name={"unitNo"}
                control={control}
                rules={
                  {
                    // required: true,
                  }
                }
                render={({ field, fieldState: { error } }) => {
                  return (
                    <TextField
                      label={t("unitNo")}
                      placeholder={t("unitNo")}
                      size="small"
                      {...field}
                      error={Boolean(error)}
                      className={"col-span-1"}
                    />
                  );
                }}
              />
            </div>
            <p className={"font-bold text-primary-80 mt-3"}>{t("meterInfo")}</p>
            <div className={"grid grid-cols-3 gap-x-2 px-2 gap-y-3"}>
              <Controller
                name={"waterMeterId"}
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <div
                      className={
                        "flex w-full justify-between items-center col-span-3 border-[1px] border-gray-30 rounded-sm px-4 py-2"
                      }
                    >
                      <div className={"flex"}>
                        <p className={"font-bold text-gray-110 w-12"}>
                          {t("meter")}
                        </p>
                        {renderSelectedMeterData()}
                      </div>
                      <Button
                        variant="outlined"
                        className={"w-[106px]"}
                        onClick={onSelectMeterClick}
                      >
                        {watch("waterMeterId")
                          ? t("replaceMeter")
                          : t("chooseMeter")}
                      </Button>
                    </div>
                  );
                }}
              />
              <Controller
                name={"installationDT"}
                control={control}
                rules={{
                  required: true,
                }}
                render={({
                  field: { onChange, value, ...restField },
                  fieldState: { error },
                }: any) => {
                  return (
                    <DatePicker
                      callback={(res) => {
                        onChange(convertDateForQueryParam(res));
                      }}
                      defaultValueDate={value}
                      {...restField}
                      showDefaultDate={false}
                      className="col-span-1"
                      defaultError={Boolean(error)}
                      label={t("installationDate")}
                    />
                  );
                }}
              />
            </div>
          </div>
          <div className="col-span-12 flex items-center justify-end mt-6">
            <Button
              className="w-20"
              loading={isLoading}
              type="submit"
              variant="contained"
            >
              {t("add")}
            </Button>
          </div>
        </form>
      </Dialog>
      {isApartmentsModalOpen && (
        <ApartmentsChooserModal
          onClose={() => {
            setIsApartmentsModalOpen(false);
          }}
          isOpen={isApartmentsModalOpen}
          onSelectApartment={onChooseApartment}
        />
      )}
      {isMetersModalOpen && (
        <MetersChooserModal
          onClose={() => {
            setIsMetersModalOpen(false);
          }}
          isOpen={isMetersModalOpen}
          onSelectMeter={onChooseMeter}
        />
      )}
    </>
  );
}
